import { Nullable } from 'constants/types'
import { AriaAttr, EventType } from 'constants/enums'
import { isMobile } from 'utils/system/device'
import { VideoPlayerStore } from './player/store'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { StickyWrapperEvent } from 'constants/enums'

const FEATURED_VIDEO_FIXED_CLASS = 'featured-video--fixed'
const FEATURED_VIDEO_MINIMIZED_CLASS = 'player-wrapper--minimized'
const FEATURED_VIDEO_FLOATING_CLASS = 'player-wrapper--float'
const FEATURED_VIDEO_MINIMIZED_TRIGGER_FACTOR = 0.2
const FEATURED_VIDEO_BOTTOM_POSITION = 24 // default spacing -> 1.5rem
const ARTICLE_LIMIT_ALERT_HEIGHT = 80 // Piano injected iframe's height -> 5.5rem / 80px
const Y_SCROLLING_INTERVAL = 500 // saves scrolling position every 500 ms

export class Sticky extends ComponentBase {
    public closeVid = false
    public trigger = 0
    public lastScrollTop = 0
    public scrollyAxisPosition = 0
    public scrollUp: boolean
    public featuredVideoBottomPosition: number
    public articleLimitAlert = false
    public footerVisible = false
    public intervalId = 0
    public observer: IntersectionObserver
    private abTest: Partial<{
        useNewStoryPageTemplate: boolean
    }> = {}
    get stickyWrapper(): Nullable<HTMLElement> {
        return this.getElement('.featured-video--fixed .player-wrapper')
    }

    get closeFeaturedVid(): Nullable<HTMLElement> {
        return this.getElement('#close-video')
    }

    get toggleFeaturedVid(): Nullable<HTMLElement> {
        return this.getElement('#toggle-video')
    }

    get toggleVideoContainer(): Nullable<HTMLElement> {
        return this.getElement('#video-container')
    }

    get header(): Nullable<HTMLElement> {
        return this.getElement<HTMLElement>('#header')
    }

    get footer(): Nullable<HTMLElement> {
        return this.getElement<HTMLElement>('#footer')
    }

    get playerEl(): Nullable<HTMLElement> {
        return this.getElement<HTMLElement>('#player')
    }

    get bottomStickyAdHeight(): number {
        return (
            this.getElement<HTMLElement>('.fixed-bottom-ad-container')
                ?.offsetHeight ?? 0
        )
    }

    get articleCounterHeight(): number {
        return (
            this.getElement<HTMLElement>('#article-counter')?.offsetHeight ?? 0
        )
    }

    private get isVideoMinimized(): boolean {
        if (!this.toggleVideoContainer) {
            return false
        }
        return this.toggleVideoContainer.classList.contains(
            FEATURED_VIDEO_MINIMIZED_CLASS
        )
    }

    public constructor(public featuredVid: HTMLElement) {
        super()
        const pageData = window.document.getElementById('page-data')
        if (pageData) {
            this.abTest = JSON.parse(pageData.innerHTML).page.abTest
        }
        // 1. Start tracking footer position
        this.observer = new IntersectionObserver(
            this.setFooterVisible.bind(this)
        )
        // 2. Set sticky trigger:  how much of a video elements in pixels has to be hidden
        // initial value request 20% or factor of 0.2
        if (this.toggleVideoContainer) {
            const height =
                this.toggleVideoContainer.getBoundingClientRect().height
            this.trigger = height * FEATURED_VIDEO_MINIMIZED_TRIGGER_FACTOR
        }

        this.dockStickyToFooter()

        // 4. Listen to tinypass injection of article limit alert
        window.addEventListener('articleLimitAlertEvent', ((
            event: CustomEvent
        ) => {
            this.articleLimitAlert = event['detail']['limit'] ? true : false
            // If page scrolled to sticky video
            // when article limit alert injected
            // reposition sticky video
            this.dockStickyToFooter()
        }) as EventListener)

        // 5. Listen to close sticky events
        if (this.closeFeaturedVid) {
            this.closeFeaturedVid.addEventListener(EventType.CLICK, () =>
                this.closeStickyVideo()
            )
        }

        // 6. Listen to minimize/maximize sticky events
        if (this.toggleFeaturedVid) {
            this.toggleFeaturedVid.addEventListener(EventType.CLICK, () =>
                this.toggleStickyVideo()
            )
        }

        // 8. Position sticky video and
        // reposition on scroll
        if (this.applyStickyBehaviour()) {
            this.stickyVideo()
        }

        // 9. Listen to sticky ad close event
        window.addEventListener('stickyAdClosedEvent', ((
            event: CustomEvent
        ) => {
            this.handleStickyAdCloseEvent(event.detail.position === 'bottom')
        }) as EventListener)

        window.addEventListener(EventType.SCROLL, () => this.stickyVideo())
        /**
         * for BC-229 : fix unwanted autoscroll to top after user exits full screen mode of video player in iPad iOS v12
         */
        if (window.navigator.userAgent.includes('iPad; CPU OS 12_')) {
            this.setTimer()
            window.addEventListener(EventType.RESIZE, () =>
                this.noScrolltoTop()
            )
            if (document.body) {
                window.addEventListener(EventType.TOUCH_START, () => {
                    this.scrollyAxisPosition = window.scrollY
                    this.setTimer()
                })
                window.addEventListener(EventType.TOUCH_END, () => {
                    this.scrollyAxisPosition = window.scrollY
                    this.setTimer()
                })
            }
            window.addEventListener(EventType.LOAD, () => this.setTimer())
            window.addEventListener(EventType.BEFORE_UNLOAD, () =>
                this.clearTimer()
            )
        }
    }

    /**
     * for BC-229 : fix unwanted autoscroll to top after user exits full screen mode of video player
     */
    public noScrolltoTop(): void {
        this.clearTimer()
        window.scrollTo(0, this.scrollyAxisPosition)
    }

    public setTimer(): void {
        this.clearTimer()
        this.intervalId = window.setInterval(() => {
            this.scrollyAxisPosition = window.scrollY
        }, Y_SCROLLING_INTERVAL)
    }

    public clearTimer(): void {
        window.clearInterval(this.intervalId)
    }

    /**
     * Set the footerVisible flag
     */
    public setFooterVisible(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            this.footerVisible = entry.isIntersecting
        }
    }

    /**
     * Set when sticky should be applied
     */
    public applyStickyBehaviour(): Nullable<boolean> {
        if (!this.header) {
            return false
        }

        const store = VideoPlayerStore.getInstance()
        let isPlaying = false
        try {
            isPlaying = store.featurePlayer?.getState() === 'playing'
            // eslint-disable-next-line no-empty
        } catch (e) {}

        if (!isPlaying && this.abTest.useNewStoryPageTemplate) {
            return false
        }

        return (
            this.featuredVid.getBoundingClientRect().top -
                this.header.offsetHeight <
            this.trigger * -1
        )
    }

    /**
     * Reposition sticky video on ad close event
     * when ad is bottom
     */
    public handleStickyAdCloseEvent(bottom: boolean): void {
        if (bottom) {
            // reposition sticky video
            this.dockStickyToFooter()
        }
    }

    /**
     * Add sticky video css class
     * reset  video behaviour on non-sticky removal
     */
    public stickyVideo(): void {
        if (this.header) {
            let stickyVisible
            if (this.applyStickyBehaviour() && !this.closeVid) {
                this.featuredVid.classList.add(FEATURED_VIDEO_FIXED_CLASS)
                stickyVisible = 'false'
            } else {
                this.featuredVid.classList.remove(FEATURED_VIDEO_FIXED_CLASS)
                // Reset to initial aria-label
                if (this.isVideoMinimized && this.toggleFeaturedVid) {
                    this.toggleFeaturedVid.setAttribute(
                        AriaAttr.LABEL,
                        'Minimize video'
                    )
                }
                if (this.toggleVideoContainer) {
                    this.toggleVideoContainer.classList.remove(
                        FEATURED_VIDEO_MINIMIZED_CLASS
                    )
                }
                stickyVisible = 'true'
            }
            if (this.closeFeaturedVid) {
                this.closeFeaturedVid.setAttribute(
                    AriaAttr.HIDDEN,
                    stickyVisible
                )
            }
            if (this.toggleFeaturedVid) {
                this.toggleFeaturedVid.setAttribute(
                    AriaAttr.HIDDEN,
                    stickyVisible
                )
            }
        }
        // Sticky should not cover footer
        this.dockStickyToFooter()
    }

    /**
     * Determine scroll direction
     * to use for docking sticky logic on mobile
     */
    public getScrollDirection(): void {
        // Get the number of pixels the document is currently scrolled along the vertical axis
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop

        // Set direction key value
        this.scrollUp = scrollTop > this.lastScrollTop ? false : true
        // Reset negative scroll for last value
        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop
    }

    /**
     * Adjust sticky video position
     * above visible footer
     */
    public dockStickyToFooter(): void {
        if (!this.footer || !this.stickyWrapper) {
            return
        }

        let dynamicStickyPosition
        // Init bottom position
        this.featuredVideoBottomPosition = this.articleLimitAlert
            ? ARTICLE_LIMIT_ALERT_HEIGHT + FEATURED_VIDEO_BOTTOM_POSITION
            : FEATURED_VIDEO_BOTTOM_POSITION
        // Get root element's height (<html>)
        const windowHeight: number = document.documentElement.clientHeight
        // Get footer's size and its position relative to the viewport
        const footerCoords = this.footer.getBoundingClientRect()

        this.observer.observe(this.footer)
        // Calculate when footer is visible
        if (this.articleLimitAlert) {
            dynamicStickyPosition = Math.round(
                windowHeight -
                    this.featuredVideoBottomPosition -
                    footerCoords.top +
                    FEATURED_VIDEO_BOTTOM_POSITION
            )
        } else {
            dynamicStickyPosition = Math.round(windowHeight - footerCoords.top)
        }

        // Calculate bottom distance for sticky video
        let spacer = this.footerVisible
            ? dynamicStickyPosition + this.featuredVideoBottomPosition
            : this.featuredVideoBottomPosition
        spacer = spacer + this.bottomStickyAdHeight + this.articleCounterHeight

        // Set bottom distance for sticky video
        this.stickyWrapper.style.bottom = `${spacer}px`

        // MOBILE logic
        if (isMobile()) {
            // Track scroll direction
            this.getScrollDirection()
            // Get sticky video wrapper's size and its position relative to the viewport
            const stickyCoords = this.stickyWrapper.getBoundingClientRect()
            const stickyFullyVisible = stickyCoords.top > 0
            if (this.footerVisible) {
                this.stickyWrapper.classList.add(FEATURED_VIDEO_FLOATING_CLASS)
            } else if (stickyFullyVisible && this.scrollUp) {
                this.stickyWrapper.classList.remove(
                    FEATURED_VIDEO_FLOATING_CLASS
                )
            }
        }
    }

    /**
     * Pause sticky video
     */
    private pauseStickyVideo(): void {
        const store = VideoPlayerStore.getInstance()
        // Attempt to pause programmatic players only (YouTube and JW)
        // Distroscale and Send to News cannot be paused
        if (store.featurePlayer && !!store.featurePlayer.isPausable()) {
            store.featurePlayer.pause()
        }
    }

    /**
     * Remove sticky video CSS class
     */
    public closeStickyVideo(): void {
        window.dispatchEvent(new CustomEvent(StickyWrapperEvent.CLOSE))
        this.featuredVid.classList.remove(FEATURED_VIDEO_FIXED_CLASS)
        const store = VideoPlayerStore.getInstance()

        if (store.featurePlayer && !store.featurePlayer.isAdPlaying) {
            this.pauseStickyVideo()
        }

        if (store.featurePlayer) {
            store.featurePlayer.dispatchAnalyticsStickyClose()
        }

        this.closeVid = true

        if (this.closeFeaturedVid) {
            this.closeFeaturedVid.setAttribute(AriaAttr.HIDDEN, 'true')
        }
        if (this.toggleFeaturedVid) {
            this.toggleFeaturedVid.setAttribute(AriaAttr.HIDDEN, 'true')
        }
    }

    /**
     * Minimize/maximize fixed sticky video
     */
    public toggleStickyVideo(): void {
        if (!this.toggleVideoContainer) {
            return
        }

        let hiddenState, toggleBtnAction
        const featured = VideoPlayerStore.getInstance().featurePlayer
        if (this.isVideoMinimized) {
            // Maximize video
            this.toggleVideoContainer.classList.remove(
                FEATURED_VIDEO_MINIMIZED_CLASS
            )
            // Prepare aria-hidden tag attribute
            hiddenState = 'false'
            toggleBtnAction = 'Minimize video'
            if (featured) {
                featured.dispatchAnalyticsStickyRestore()
            }
        } else {
            window.dispatchEvent(new CustomEvent(StickyWrapperEvent.MINIMIZE))
            // Minimize video
            this.toggleVideoContainer.classList.add(
                FEATURED_VIDEO_MINIMIZED_CLASS
            )
            // Prepare aria-hidden tag attribute
            hiddenState = 'true'
            toggleBtnAction = 'Restore video'
            this.pauseStickyVideo()
            if (featured) {
                featured.dispatchAnalyticsStickyMinimize()
            }
        }
        // Set ARIA attributes
        if (this.playerEl) {
            this.playerEl.setAttribute(AriaAttr.HIDDEN, hiddenState)
        }
        if (this.toggleFeaturedVid) {
            this.toggleFeaturedVid.setAttribute(AriaAttr.LABEL, toggleBtnAction)
        }
        // Minimized sticky on mobile - close button hidden
        if (isMobile() && this.closeFeaturedVid) {
            this.closeFeaturedVid.setAttribute(AriaAttr.HIDDEN, hiddenState)
        }
    }
}
